/**
 * useSelectionSync - Sync Selections with API Data
 *
 * Single responsibility: Loading existing selections from API response.
 * Syncs local state with server data when pickem details are loaded.
 */

import { useEffect, useRef } from 'react';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import {
  QUESTION_TYPES,
  OPTION_SELECTION_QUESTION_TYPES,
  OVER_UNDER_QUESTION_TYPES,
} from '@/constants/question-types';
import { getQuestionId, getOptionId, toId } from '@/utils/id-helpers';
import type { SelectionState } from './useSelectionState';
import { overUnderAnswerOf } from '@/utils/over-under-option';

/**
 * Syncs selection state with API data
 *
 * @param pickemDetails - The pickem details from API
 * @param setSelections - Function to update selections state
 */
export function useSelectionSync(
  pickemDetails: PickemDetailResponse | undefined,
  setSelections: React.Dispatch<React.SetStateAction<SelectionState>>
) {
  // Track whether we've already synced to prevent overwriting manual changes
  const hasSynced = useRef(false);

  useEffect(() => {
    // Only sync once when data is first loaded
    if (!pickemDetails?.pickem.questions || hasSynced.current) return;

    const existingTeams: Record<string, string> = {};
    const existingProps: Record<string, 'yes' | 'no'> = {};
    const existingOverUnder: Record<string, 'over' | 'under'> = {};
    let existingTieBreaker = '';

    // Load team and prop selections from questions
    pickemDetails.pickem.questions.forEach(question => {
      if (!question.user_selection) return;

      const questionId = getQuestionId(question);

      // Handle option-pick questions — team / player / custom / cross-fixture
      // leader / moneyline / spread — all stored in the `teams` selection
      // bucket on the frontend, keyed by question_id (each stores an option_id
      // so they rehydrate the same way — DKPLAT-354, DKPLAT-500).
      if (OPTION_SELECTION_QUESTION_TYPES.includes(question.question_type)) {
        existingTeams[questionId] = toId(question.user_selection);
      }
      // Handle prop questions
      else if (question.question_type === QUESTION_TYPES.PROP) {
        // Convert both IDs to strings for comparison to handle string/number mismatches
        const userSelectionId = toId(question.user_selection);
        const selectedOption = question.options.find(opt => getOptionId(opt) === userSelectionId);

        if (selectedOption) {
          // First try to detect by title
          const optTitle = selectedOption.title_en?.toLowerCase();
          if (optTitle?.includes('yes')) {
            existingProps[questionId] = 'yes';
          } else if (optTitle?.includes('no')) {
            existingProps[questionId] = 'no';
          } else {
            // Fall back to position: first option = yes, second = no
            const optionIndex = question.options.findIndex(
              opt => getOptionId(opt) === userSelectionId
            );
            existingProps[questionId] = optionIndex === 0 ? 'yes' : 'no';
          }
        }
      }
      // Handle over/under questions (incl. match-level Total)
      else if (OVER_UNDER_QUESTION_TYPES.includes(question.question_type)) {
        // Convert both IDs to strings for comparison to handle string/number mismatches
        const userSelectionId = toId(question.user_selection);
        const selectedOption = question.options.find(opt => getOptionId(opt) === userSelectionId);

        if (selectedOption) {
          existingOverUnder[questionId] = overUnderAnswerOf(question, selectedOption);
        }
      }
    });

    // Load tiebreaker custom_value from selections array
    if (pickemDetails?.pickem.selections && Array.isArray(pickemDetails.pickem.selections)) {
      const tiebreakerQuestion = pickemDetails.pickem.questions.find(
        q => q.question_type === QUESTION_TYPES.TIEBREAKER
      );

      if (tiebreakerQuestion) {
        const tiebreakerQuestionId = getQuestionId(tiebreakerQuestion);
        const tiebreakerSelection = pickemDetails.pickem.selections.find(
          s =>
            s.question_id === tiebreakerQuestionId || String(s.question_id) === tiebreakerQuestionId
        );

        if (
          tiebreakerSelection?.custom_value !== null &&
          tiebreakerSelection?.custom_value !== undefined
        ) {
          existingTieBreaker = String(tiebreakerSelection.custom_value);
        }
      }
    }

    // Update state with loaded selections
    setSelections(prev => ({
      ...prev,
      teams: existingTeams,
      props: existingProps,
      overUnder: existingOverUnder,
      tieBreaker: existingTieBreaker,
    }));

    // Mark that we've synced to prevent overwriting manual changes
    hasSynced.current = true;
  }, [pickemDetails, setSelections]);
}
